import type { ReactNode } from 'react';
import { TONE_VAR, type StatusTone } from '@/theme/statusTone';
import { cn } from '@/lib/utils';

/**
 * A filled badge, for the ONE primary state of a detail record.
 *
 * The tint is `color-mix` against the tone rather than a second palette of pre-mixed
 * background colours: one tone token produces both the fill and the border, in either
 * theme, with nothing to keep in sync.
 *
 * ⚠️ TESTING: jsdom's `cssstyle` validates colour values and silently DROPS
 * `color-mix(...)`, so `toHaveStyle({ backgroundColor: … })` fails here for a reason that
 * has nothing to do with this component. Assert `data-tone`.
 */
export type StatusPillProps = {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
  /**
   * ⚠️ [C6a §4] DECLARED AND FORWARDED, BECAUSE A HYPHENATED JSX ATTRIBUTE IS NEVER TYPE-CHECKED.
   * `pages/DutyFill` has passed `data-testid="fill-discharged"` since [C3c §5c] and this component
   * dropped it on the floor — the compiler accepts any `data-*` on any component — so the "Your part
   * is done" pill carried no handle and no test could find it. Nothing had asserted it, which is how.
   */
  'data-testid'?: string;
};

export const StatusPill = ({ tone, children, className, 'data-testid': testId }: StatusPillProps) => (
  <span
    data-tone={tone}
    data-testid={testId}
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
      'whitespace-nowrap text-foreground',
      className,
    )}
    style={{
      backgroundColor: `color-mix(in srgb, ${TONE_VAR[tone]} 16%, transparent)`,
      borderColor: `color-mix(in srgb, ${TONE_VAR[tone]} 40%, transparent)`,
    }}
  >
    <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: TONE_VAR[tone] }} aria-hidden />
    {children}
  </span>
);
